import { TitleCasePipe } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { UserLevel, userLevelIsEnough } from '@app/core/models/user-level';
import { BackendSystemService } from '@app/portal/services/backend-system.service';
import { loggerFor } from '@app/utils/logger';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'portal-backend-system-switcher',
  templateUrl: './backend-system-switcher.component.html',
  standalone: false,
})
export class BackendSystemSwitcherComponent implements OnInit, OnDestroy {
  isHydraEmployed: boolean = false;
  targetSystems: { value: string; label: string }[] = [];

  private targetSystems$: Observable<{ value: string; label: string }[]>;
  private logger = loggerFor('BackendSystemSwitcherComponent');

  @Output()
  onChange: EventEmitter<string> = new EventEmitter<string>();

  form = this.fb.group({
    targetSystem: '',
  });

  constructor(
    private authenticationService: AuthenticationService,
    private targetSystemService: BackendSystemService,
    private titleCasePipe: TitleCasePipe,
    private fb: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    const currentBackendSystem$ = this.targetSystemService.currentBackendSystem.data$.pipe(
      untilDestroyed(this),
      distinctUntilChanged(),
      filter((_) => _ !== '')
    );

    // if user is logged-in and auth-user level is service provider admin | system admin fetch target systems
    this.authenticationService.authUser$
      .pipe(
        untilDestroyed(this),
        filter((_) => _.username !== undefined)
      )
      .subscribe((authUser) => {
        this.logger.debug('current auth-user is', authUser);
        if (userLevelIsEnough(UserLevel.ServiceProviderAdmin, authUser)) {
          this.targetSystems$ = this.targetSystemService.backendSystems.data$.pipe(
            untilDestroyed(this),
            map((targetSystems) =>
              // t-mobile has additional `comm5` system in hydra for SIP testing. we hide it here because there is no portal for it.
              targetSystems.systems
                .filter((ts) => ts !== 'comm5')
                .map((ts) => ({
                  value: ts,
                  label: this.titleCasePipe.transform(ts),
                }))
            )
          );

          currentBackendSystem$.pipe(untilDestroyed(this)).subscribe((currentBackendSystem) => {
            this.logger.info('current target system', currentBackendSystem);
            this.onChange.emit(currentBackendSystem);
            this.form.patchValue({
              targetSystem: currentBackendSystem,
            });
            this.form.controls.targetSystem.enable();
          });

          combineLatest([currentBackendSystem$, this.targetSystems$])
            .pipe(untilDestroyed(this))
            .subscribe(([currentBackendSystem, targetSystems]) => {
              if (
                !!currentBackendSystem &&
                currentBackendSystem !== '' &&
                !!targetSystems &&
                targetSystems.length > 0
              ) {
                this.isHydraEmployed = true;
              }

              this.targetSystems = targetSystems;
            });
        }
      });
  }

  ngOnDestroy() {
    this.logger.debug('ngOnDestroy');
  }

  onValueChange() {
    this.logger.info('changing target system to', this.form.value.targetSystem);
    this.onChange.emit(this.form.value.targetSystem);
    this.form.controls.targetSystem.disable();

    this.targetSystemService
      .switchBackendSystem({
        newBackendSystem: this.form.controls.targetSystem.value,
      })
      .subscribe();
  }
}
